import * as React from 'react';

import { IRouter, Link } from 'react-router';
import { url, submitForm } from '../../util';
import { auth } from '../../util/auth';

import Input from '../form/Input';
import BackButton from '../common/BackButton';

import { Digits, NotEmpty } from '../form/Constraints';

import { IInputChangeHandler, IFieldError, IError, IOwner, IRouterContext } from '../../types';

interface IOwnerEditorProps {
  initialOwner?: IOwner;
}

interface IOwnerEditorState {
  owner?: IOwner;
  error?: IError;
};

export default class OwnerEditor extends React.Component<IOwnerEditorProps, IOwnerEditorState> {

  context: IRouterContext;
  private _isMounted: boolean = false;
  private _isNavigating: boolean = false;

  static contextTypes = {
    router: React.PropTypes.object.isRequired
  };

  constructor(props: IOwnerEditorProps) {
    super(props);
    this.onInputChange = this.onInputChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.state = {
      owner: props.initialOwner ? Object.assign({}, props.initialOwner) : undefined
    };
  }

  componentWillReceiveProps(nextProps: IOwnerEditorProps) {
    // Update state when initialOwner prop changes (e.g., when EditOwnerPage loads the owner)
    if (nextProps.initialOwner) {
      // Check if owner data actually changed (by comparing IDs or checking if it's a new object)
      const currentOwner = this.state.owner;
      const nextOwner = nextProps.initialOwner;
      
      // Update if:
      // 1. We don't have an owner yet, OR
      // 2. The owner ID changed, OR
      // 3. It's a different object reference (EditOwnerPage loaded new data)
      if (!currentOwner || 
          (currentOwner.id !== nextOwner.id) || 
          (nextProps.initialOwner !== this.props.initialOwner)) {
        console.log('OwnerEditor: Updating owner from props', nextOwner);
        this.setState({
          owner: Object.assign({}, nextOwner)
        });
      }
    }
  }

  componentDidMount() {
    this._isMounted = true;
    this._isNavigating = false;
  }

  componentWillUnmount() {
    this._isMounted = false;
    this._isNavigating = false;
  }

  shouldComponentUpdate(nextProps: IOwnerEditorProps, nextState: IOwnerEditorState) {
    // Prevent any updates if we're navigating
    if (this._isNavigating) {
      return false;
    }
    // Always allow updates if props or state changed
    return true;
  }

  onSubmit(event) {
    event.preventDefault();
    event.stopPropagation();

    // Prevent multiple submissions
    if (this._isNavigating) {
      return;
    }

    const { owner } = this.state;

    // Extract only the fields required by OwnerFieldsDto
    const ownerFields = {
      firstName: owner.firstName,
      lastName: owner.lastName,
      address: owner.address,
      city: owner.city,
      telephone: owner.telephone
    };

    const url = owner.isNew ? '/api/owners' : '/api/owners/' + owner.id;
    console.log('Submitting owner fields:', ownerFields);
    
    submitForm(owner.isNew ? 'POST' : 'PUT', url, ownerFields, (status, response) => {
      // Check if component is still mounted and not already navigating
      if (!this._isMounted || this._isNavigating) {
        return;
      }

      if (status === 200 || status === 201 || status === 204) {
        // For updates (204), we don't get the owner back in response, so use the owner from state
        // For creates (201), we get the new owner in response
        const updatedOwner = (status === 204 && owner && owner.id) 
          ? owner 
          : (response as IOwner);
        
        const ownerId = updatedOwner.id || owner.id;
        console.log('Owner saved successfully. Status:', status, 'Owner ID:', ownerId);
        
        if (!ownerId) {
          console.error('No owner ID available for navigation');
          return;
        }
        
        // Mark as navigating IMMEDIATELY to prevent any React updates
        this._isNavigating = true;
        // Use a form-based navigation to avoid React update conflicts
        // Navigate to owner details page (shows owner info and allows adding pets)
        const form = document.createElement('form');
        form.method = 'GET';
        form.action = `/owners/${ownerId}`;
        document.body.appendChild(form);
        // Submit the form after a tiny delay to ensure React finishes current cycle
        setTimeout(() => {
          form.submit();
        }, 10);
      } else if (status === 401) {
        // Unauthorized - user needs to log in again
        console.error('Unauthorized - redirecting to login');
        auth.clearAuth();
        // Mark as navigating IMMEDIATELY
        this._isNavigating = true;
        // Use form-based navigation
        const form = document.createElement('form');
        form.method = 'GET';
        form.action = '/login';
        document.body.appendChild(form);
        setTimeout(() => {
          form.submit();
        }, 10);
      } else {
        console.error('Error saving owner. Status:', status, 'Response:', response);
        // Only update state if we're not navigating and component is still mounted
        if (this._isMounted && !this._isNavigating) {
          // Ensure error object has the correct structure
          const error = response && typeof response === 'object' 
            ? response 
            : { fieldErrors: {}, message: response?.message || 'Failed to save owner' };
          // Direct setState - no need for requestAnimationFrame since we're not navigating
          this.setState({ error });
        }
      }
    });
  }

  onInputChange(name: string, value: string, fieldError: IFieldError) {
    // Don't update state if we're navigating
    if (this._isNavigating || !this._isMounted) {
      return;
    }
    
    const { owner, error } = this.state;
    const modifiedOwner = Object.assign({}, owner, { [name]: value });
    const newFieldErrors = error ? Object.assign({}, error.fieldErrors, {[name]: fieldError }) : {[name]: fieldError };
    this.setState({
      owner: modifiedOwner,
      error: { fieldErrors: newFieldErrors }
    });
  }

  render() {
    // Don't render if we're navigating - prevents React update conflicts
    if (this._isNavigating) {
      return <div>Redirecting...</div>;
    }
    
    const { owner, error } = this.state;
    
    // Don't render form until owner is initialized
    if (!owner) {
      return <div>Loading...</div>;
    }
    
    return (
      <span>
        <BackButton />
        <h2>{owner.isNew ? 'New Owner' : 'Edit Owner'}</h2>
        <form className='form-horizontal' method='POST' action={url('/api/owner')}>
          <div className='form-group has-feedback'>
            <Input object={owner} error={error} constraint={NotEmpty} label='First Name' name='firstName' onChange={this.onInputChange} />
            <Input object={owner} error={error} constraint={NotEmpty} label='Last Name' name='lastName' onChange={this.onInputChange} />
            <Input object={owner} error={error} constraint={NotEmpty} label='Address' name='address' onChange={this.onInputChange} />
            <Input object={owner} error={error} constraint={NotEmpty} label='City' name='city' onChange={this.onInputChange} />
            <Input object={owner} error={error} constraint={Digits(10)} label='Telephone' name='telephone' onChange={this.onInputChange} />
          </div>
          <div className='form-group'>
            <div className='col-sm-offset-2 col-sm-10'>
              <button className='btn btn-default' type='button' onClick={this.onSubmit} disabled={this._isNavigating}>{owner.isNew ? 'Add Owner' : 'Update Owner'}</button>
            </div>
          </div>
        </form>
      </span>
    );
  }
}
